import { Box, Checkbox, Tooltip } from '@mui/material';
import { getDocumentCategoriesRequest } from 'api/requests/documents-requests';
import { UPButtonStyled } from 'components/button/styles';
import UPDateRangePicker from 'components/date-range-picker/UPDateRangePicker';
import { UPDropdownItem } from 'components/dropdown';
import UPDropdownWithSearch from 'components/dropdown-with-search';
import UPSearchInput from 'components/input-with-search';
import { UPInputLabel } from 'components/input/styles';
import { CompanyRole } from 'constants/CompanyRole';
import { toNumber } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import {
  getAllUserCompaniesAsDropdownItemsAction,
  getCompaniesAsDropdownItemsAction,
} from 'store/actions/company-actions';
import {
  getDocumentsAction,
  getDocumentsPayloadAction,
  syncExpressoInvoicesAction,
} from 'store/actions/documents-action';
import { getEmployeesDropdownItems } from 'store/actions/employee-actions';
import { setMenuStatusAction } from 'store/actions/menu-actions';
import { companiesDropdownItemsSelector, userCompaniesDropdownItemsSelector } from 'store/selectors/company-selectors';
import { documentsSelector } from 'store/selectors/documents-selectors';
import { employeesDropdownItemsSelector } from 'store/selectors/employee-selector';
import { activeMenuItemSelector } from 'store/selectors/menu-selectors';
import { hasSelectedCompanySelector, profileSelector } from 'store/selectors/profile-selectors';
import {
  COMPANY__COMPANY,
  DOCUMENTS__DOCUMENT_CATEGORY,
  DOCUMENTS__SYNC_WITH_EXPRESSO,
  DOCUMENTS__SYNC_WITH_EXPRESSO_INCLUDE_EMPLOYEE_DOCS,
  GENERAL__SEARCH,
  GENERAL__SELECT,
  MENU__DOCS,
  MENU__EMPLOYEES,
} from 'translations/constants';
import { treatAsUTC } from 'utils/helpers';
import { ReactComponent as AddDocumentIcon } from '../../assets/icons/AddDocumentIcon.svg';
import { ReactComponent as RefreshIcon } from '../../assets/icons/Refresh.svg';
import AddNewDocumentModal from './AddNewDocumentModal';
import DocumentsGridView from './DocumentsGridView';
import { AddNewDocumentButton, FilterItem, FiltersContainer, InvoiceFilterItem } from './styles';

const Documents = (): JSX.Element => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const state = location.state as { category: number; companyId?: number };

  const activeItem = useAppSelector(activeMenuItemSelector);
  const profile = useAppSelector(profileSelector);
  const isAdmin = profile.role === CompanyRole.ADMIN;
  const isAdminOrAccountManager = profile.role === CompanyRole.ADMIN || profile.role === CompanyRole.ACCOUNT_MANAGER;
  const isEmployee = profile.role === CompanyRole.EMPLOYEE;
  const companiesDropdownItems = useAppSelector(companiesDropdownItemsSelector);
  const userCompaniesDropdownItems = useAppSelector(userCompaniesDropdownItemsSelector);
  const userCompanyId = useAppSelector(profileSelector).companyId;
  const documents = useAppSelector(documentsSelector);

  const [companyId, setCompanyId] = useState<string>(userCompanyId !== null ? userCompanyId.toString() : undefined);
  const [employeeId, setEmployeeId] = useState<string>(undefined);
  const [categorySelected, setCategorySelected] = useState<number>(null);
  const [openAddNewDocumentModal, setOpenAddNewDocumentModal] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<[Date, Date]>([null, null]);
  const [includeEmployeeDocs, setIncludeEmployeeDocs] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeSearchQuery, setActiveSearchQuery] = useState<string>(''); // The search query actually used for filtering
  const employeesDropdownItems = useAppSelector(employeesDropdownItemsSelector);
  const [documentCategories, setDocumentCategories] = useState<UPDropdownItem[]>([]);
  const [displayDocumentCategories, setDisplayDocumentCategories] = useState<UPDropdownItem[]>([]);
  const [startDate, endDate] = dateRange;

  const mapCategories = useCallback(
    (categories: UPDropdownItem[]): UPDropdownItem[] => {
      return (
        categories?.map(item => {
          return {
            value: item.value,
            label: t(item.label),
          };
        }) || []
      );
    },
    [t],
  );

  useEffect(() => {
    getDocumentCategoriesRequest().then(r => {
      if (!r) return;

      const uploadCategories = mapCategories(r.uploadCategories);

      setDocumentCategories(uploadCategories);

      const displayCategories = mapCategories(r.readCategories);

      setDisplayDocumentCategories(displayCategories);
    });
  }, [mapCategories]);

  useEffect(() => {
    if (!isEmployee && companyId) dispatch(getEmployeesDropdownItems(+companyId));
  }, [companyId, dispatch, isEmployee]);

  const onDownloadInvoices = () => {
    dispatch(
      syncExpressoInvoicesAction({
        companyId: toNumber(companyId),
        startDate: startDate ? treatAsUTC(startDate) : startDate,
        endDate: endDate ? treatAsUTC(endDate) : endDate,
        addEmployeeDocs: includeEmployeeDocs,
      }),
    ).then(() => {
      dispatch(
        getDocumentsAction({
          companyId: toNumber(companyId),
          category: categorySelected,
          employeeId: toNumber(employeeId),
          startDate: startDate ? treatAsUTC(startDate) : startDate,
          endDate: endDate ? treatAsUTC(endDate) : endDate,
          searchQuery: activeSearchQuery || undefined,
          page: 1,
          pageSize: 25,
        }),
      );
    });
  };

  const handleChange = () => {
    setIncludeEmployeeDocs(!includeEmployeeDocs);
  };

  useEffect(() => {
    dispatch(getDocumentsPayloadAction([]));
  }, [dispatch]);

  useEffect(() => {
    if (location.state) {
      setCategorySelected(state.category);
    }
  }, [location, state]);

  useEffect(() => {
    if (location.state && state.companyId && isAdmin) {
      setCompanyId(state.companyId.toString());
    }
  }, [isAdmin, location, state]);

  useEffect(() => {
    if (activeItem !== t(MENU__DOCS)) {
      dispatch(setMenuStatusAction(t(MENU__DOCS)));
    }
  }, [activeItem, dispatch, t]);

  useEffect(() => {
    if (isAdminOrAccountManager) {
      dispatch(getCompaniesAsDropdownItemsAction());
    }

    if (isEmployee) {
      dispatch(getAllUserCompaniesAsDropdownItemsAction(profile.id));
    }
  }, [isAdminOrAccountManager, isEmployee, profile, dispatch]);

  // Removed the useEffect that was fetching documents here -
  // DocumentsGridView now handles all data fetching to avoid duplicate calls

  const hasSelectedCompany = useAppSelector(hasSelectedCompanySelector);
  useEffect(() => {
    if (isEmployee && hasSelectedCompany && profile?.currentCompany) {
      setCompanyId(profile.currentCompany.toString());
    }
  }, [isEmployee, hasSelectedCompany, profile?.currentCompany]);

  return (
    <Box>
      <FiltersContainer>
        <AddNewDocumentButton
          text={
            <span style={{ marginTop: '2.5px', height: '25px' }}>
              <span style={{ verticalAlign: 'top' }}> + </span>
              <AddDocumentIcon />
            </span>
          }
          onClick={() => setOpenAddNewDocumentModal(true)}
          disabled={isAdminOrAccountManager && !companyId}
        />
        {isAdminOrAccountManager && (
          <FilterItem>
            <UPDropdownWithSearch
              value={companyId}
              onChange={selectedItem => {
                setCompanyId(selectedItem);
                setSearchQuery('');
                setActiveSearchQuery(''); // Also clear the active search query
                if (!selectedItem) {
                  dispatch(getDocumentsPayloadAction([]));
                }
              }}
              placeholder={t(GENERAL__SELECT)}
              items={companiesDropdownItems}
              label={t(COMPANY__COMPANY)}
              disableClearable={false}
            />
          </FilterItem>
        )}
        <FilterItem>
          <UPSearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            onSearch={() => {
              // Update the active search query which will trigger DocumentsGridView to refetch
              setActiveSearchQuery(searchQuery);
            }}
            label={t(GENERAL__SEARCH)}
            placeholder={t(GENERAL__SEARCH)}
            disabled={!companyId}
          />
        </FilterItem>
        <FilterItem>
          <UPDropdownWithSearch
            value={categorySelected}
            hasDefaultValue
            onChange={selectedItem => setCategorySelected(toNumber(selectedItem))}
            placeholder={t(GENERAL__SELECT)}
            items={displayDocumentCategories}
            label={t(DOCUMENTS__DOCUMENT_CATEGORY)}
            disableClearable={false}
          />
        </FilterItem>
        {!isEmployee && (
          <FilterItem>
            <UPDropdownWithSearch
              value={employeeId}
              onChange={selectedItem => setEmployeeId(selectedItem)}
              placeholder={t(GENERAL__SELECT)}
              items={employeesDropdownItems}
              label={t(MENU__EMPLOYEES)}
              disableClearable={false}
            />
          </FilterItem>
        )}
        <FilterItem>
          <UPDateRangePicker
            month={new Date().getMonth()}
            startDate={startDate}
            endDate={endDate}
            setDateRange={setDateRange}
          />
        </FilterItem>
        {categorySelected === 2 && isAdminOrAccountManager && (
          <InvoiceFilterItem>
            <UPInputLabel>{t(DOCUMENTS__SYNC_WITH_EXPRESSO_INCLUDE_EMPLOYEE_DOCS)}</UPInputLabel>
            <Checkbox
              onChange={handleChange}
              sx={{ padding: '0px', width: '16px', height: '16px', position: 'relative', top: '8px', left: '8px' }}
            />
            <Tooltip title={t(DOCUMENTS__SYNC_WITH_EXPRESSO)}>
              <UPButtonStyled
                onClick={() => onDownloadInvoices()}
                sx={{ backgroundColor: '#008C15', cursor: 'pointer', left: '75px', top: '8px' }}
                disabled={isAdminOrAccountManager && companyId === undefined}
              >
                <RefreshIcon />
              </UPButtonStyled>
            </Tooltip>
          </InvoiceFilterItem>
        )}
      </FiltersContainer>
      {openAddNewDocumentModal && (
        <AddNewDocumentModal
          onClose={() => setOpenAddNewDocumentModal(false)}
          companyId={toNumber(companyId)}
          categories={documentCategories}
          category={categorySelected}
          employeeId={toNumber(employeeId)}
          startDate={startDate}
          endDate={endDate}
          searchQuery={activeSearchQuery}
        />
      )}
      {companyId && (
        <DocumentsGridView
          companyId={toNumber(companyId)}
          category={categorySelected}
          employeeId={toNumber(employeeId)}
          startDate={startDate}
          endDate={endDate}
          searchQuery={activeSearchQuery}
          modalIsOpen={openAddNewDocumentModal}
        />
      )}
    </Box>
  );
};

export default Documents;
